import utm from "modules/utm";
import formSelectTemplate from "./FormSelect.hbs";
import { Block } from "modules/Block";
import { replaceHiddenInputValues } from 'modules/replaceHiddenInputValues';
import { replaceSentFromFormValues } from 'modules/replaceSentFromFormValues';

export class FormBlock extends Block<typeof FormBlock> {
    static readonly className: string = "js-form-block";

    constructor(rootElement: HTMLElement, index: number) {
        super(rootElement, index);

        this.deduplicateFormIds();
        this.styleButtons();
        this.renderCustomSelect();
        this.nativeSelects();
        const utmParams = utm.getUtmTagsFromUrlOrCookie();
        this.addUTMValuesToHiddenInputs(utmParams);
        replaceHiddenInputValues();
        replaceSentFromFormValues();

        const hasHiddenCountryCode = this.rootElement.querySelector('input.country-code[type="hidden"]');
        if (hasHiddenCountryCode) {
            this.bindCountryCodeToHiddenInput();
        } else {
            this.phoneNumberCountryCodeConcat();
        }

        this.preventPostSubmitScroll();
        this.resetCustomSelects();
    }

    // Prevents label/input cross-linking when the same EpiServer form is rendered multiple times
    deduplicateFormIds = (): void => {
        const formEl = this.rootElement.querySelector<HTMLElement>('[data-f-type="form"]')
            ?? this.rootElement.querySelector<HTMLElement>('.EPiServerForms');
        if (!formEl?.id) return;

        const originalRootId = formEl.dataset.originalFormId ?? formEl.id;
        const matchingRoots = Array.from(document.querySelectorAll<HTMLElement>('[data-f-type="form"], .EPiServerForms'))
            .filter(el => (el.dataset.originalFormId ?? el.id) === originalRootId);
        const instanceIndex = matchingRoots.indexOf(formEl);

        formEl.dataset.originalFormId = originalRootId;
        if (formEl.dataset.formIdsDeduplicated === 'true') return;
        formEl.dataset.formIdsDeduplicated = 'true';
        if (instanceIndex <= 0) return;

        const suffix = `__fi${instanceIndex}`;
        const idMap = new Map<string, string>();
        this.rootElement.querySelectorAll<HTMLElement>('[id]').forEach((element) => {
            if (element === formEl) return;
            idMap.set(element.id, `${element.id}${suffix}`);
        });
        this.rootElement.querySelectorAll<HTMLElement>('[id]').forEach((element) => {
            const replacement = idMap.get(element.id);
            if (replacement) element.id = replacement;
        });
        this.rootElement.querySelectorAll<HTMLElement>('[for]').forEach((element) => {
            const original = element.getAttribute('for');
            const replacement = original ? idMap.get(original) : undefined;
            if (replacement) element.setAttribute('for', replacement);
        });
        ['aria-describedby', 'aria-labelledby'].forEach((attribute) => {
            this.rootElement.querySelectorAll<HTMLElement>(`[${attribute}]`).forEach((element) => {
                const tokens = element.getAttribute(attribute)?.split(/\s+/).filter(Boolean);
                if (tokens) {
                    element.setAttribute(attribute, tokens.map(token => idMap.get(token) ?? token).join(' '));
                }
            });
        });
    };

    resetCustomSelects = (): void => {
        this.rootElement.querySelectorAll<HTMLElement>('[data-f-type="form"], form').forEach((form) => {
            form.addEventListener('reset', () => {
                requestAnimationFrame(() => {
                    this.rootElement.querySelectorAll<HTMLElement>('.form-select').forEach((formSelect) => {
                        formSelect.querySelectorAll<HTMLElement>('li.selected').forEach((li) => li.classList.remove('selected'));
                        const span = formSelect.querySelector<HTMLElement>('.form-header span[data-title]');
                        if (span) span.textContent = span.dataset.title ?? '';
                    });
                });
            });
        });
    };

    preventPostSubmitScroll = (): void => {
        this.rootElement.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (!target.closest('button[type="submit"], button[type="reset"]')) return;

            // eslint-disable-next-line @typescript-eslint/unbound-method -- saving prototype method reference for later restoration, not calling it
            const orig = { scrollTo: window.scrollTo.bind(window), scrollIntoView: Element.prototype.scrollIntoView };
            window.scrollTo = () => { };
            Element.prototype.scrollIntoView = function () { };
            setTimeout(() => {
                window.scrollTo = orig.scrollTo;
                Element.prototype.scrollIntoView = orig.scrollIntoView;
            }, 1000);
        }, { capture: true });
    };

	addUTMValuesToHiddenInputs = (utmParams: string[][]) => {
		const utmMap = new Map<string, string>();

		utmParams.forEach((entry) => {
			if (entry.length < 2) return;
			const [key, value] = entry;
			utmMap.set(key, value);
		});

		this.rootElement
			.querySelectorAll<HTMLInputElement>('input[type="hidden"][value^="utm_"]')
			.forEach((input) => {
				const utmKey = input.value;
				input.value = utmMap.get(utmKey) ?? "";
			});
	};

    cookieConsent = () => {
        const cookieBanner = document.getElementById("onetrust-consent-sdk");
        if (cookieBanner != null) {
            const allButtons = cookieBanner.getElementsByTagName(
                "button"
            );

            for (let i = 0; i < allButtons.length; i++) {
                allButtons[i].onclick = () => this.refreshPage();
            }
        }
    };

    refreshPage = () => {
        location.reload();
    };

    styleButtons = () => {
        const submitButtons = this.rootElement.querySelectorAll(`.Form__MainBody .FormSubmitButton`);
        const resetButtons = this.rootElement.querySelectorAll(`.Form__MainBody .FormResetButton`);

        const createArrowSvg = (): SVGSVGElement => {
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
            use.setAttribute('href', '/images/securitas.spritemap.svg#sprite-arrow-forward');
            svg.appendChild(use);
            return svg;
        };

        submitButtons.forEach((button) => {
            if (button.nextElementSibling?.classList.contains("FormResetButton")) {
                const wrapper = document.createElement("div");
                wrapper.classList.add("Form__Element", "buttons-wrapper");
                button.before(wrapper);
                wrapper.appendChild(button);
            }

            if (button.querySelector('span')) return;
            button.classList.add('button', 'button__tertiary');
            const text = button.textContent ?? '';
            button.textContent = '';
            const span = document.createElement('span');
            span.textContent = text;
            button.appendChild(createArrowSvg());
            button.appendChild(span);
            button.addEventListener('click', () => (button as HTMLElement).blur());
        });

        resetButtons.forEach((input) => {
            if (input.previousElementSibling?.classList.contains("buttons-wrapper")) {
                input.previousElementSibling.appendChild(input);
            }

            const btn = document.createElement('button');
            btn.type = 'reset';
            btn.className = input.className;
            btn.classList.add('button', 'button__secondary');
            const text = (input as HTMLInputElement).value || input.textContent || '';
            const span = document.createElement('span');
            span.textContent = text;
            btn.appendChild(createArrowSvg());
            btn.appendChild(span);
            btn.addEventListener('click', () => btn.blur());
            input.replaceWith(btn);
        });
    };

    renderCustomSelect = () => {
        const selects = this.rootElement.querySelectorAll(".Form__MainBody select");
        let customSelectIdTracker = 0;
        selects.forEach((select) => {
            const selectOptions: NodeListOf<HTMLOptionElement> =
                select.querySelectorAll("option:not([disabled='disabled'])");

            const data: {
                selectTitle: string;
                selectMultiple: boolean;
                selectedString: string;
                selectedFlag: string | null;
                isCountrySelect: boolean;
                selectOptions: Array<{
                    optionValue: string;
                    optionText: string;
                    optionFlag?: string | null;
                    isSelected: boolean;
                }>;
            } = {
                selectTitle:
                    (
                        select.querySelector("option[disabled='disabled']") as HTMLOptionElement
                    )?.innerText ?? "",
                selectMultiple: false,
                selectedString: "",
                selectedFlag: null,
                isCountrySelect: select.classList.contains("country-select"),
                selectOptions: [],
            };

            // Check if select is multiselect
            if (select.hasAttribute("multiple")) {
                data.selectMultiple = true;
            }

            selectOptions.forEach((selectOption) => {
                const selectOptionValue = selectOption.value;
                const selectOptionText = selectOption.innerText;
                const optionFlag = selectOption.getAttribute("data-flag");

                const isSelected = selectOption.selected;

                if (selectOption.selected) {
                    if (data.isCountrySelect) {
                        data.selectedString = selectOptionValue;
                        data.selectedFlag = optionFlag;
                    } else if (data.selectedString === "") {
                        data.selectedString = selectOptionText;
                    } else {
                        data.selectedString += `, ${selectOptionText}`;
                    }
                }

                const obj = {
                    optionValue: selectOptionValue,
                    optionText: selectOptionText,
                    isSelected: isSelected,
                    optionFlag: optionFlag,
                };

                if (!selectOption.disabled) {
                    data.selectOptions.push(obj);
                }
            });

            const formSelectWrapperNode = document.createElement("div");
            formSelectWrapperNode.classList.add("form-select");
            formSelectWrapperNode.setAttribute("tabindex", "0"); // Converting number to string
            formSelectWrapperNode.setAttribute("role", "listbox");
            formSelectWrapperNode.setAttribute(
                "aria-controls",
                `custom-select-${customSelectIdTracker}`
            ); // Converting number to string
            formSelectWrapperNode.setAttribute("aria-expanded", "false"); // Converting boolean to string

            select.insertAdjacentElement("beforebegin", formSelectWrapperNode);

            formSelectWrapperNode.innerHTML = formSelectTemplate(data);

            this.addSearchFilter(formSelectWrapperNode);
            this.toggleCustomSelect(formSelectWrapperNode);
            this.handleOptionSelect(formSelectWrapperNode, data.selectMultiple);
            customSelectIdTracker++;
        });
    };

    addSearchFilter = (selectWrapper: HTMLDivElement) => {
        const originalSelect = selectWrapper.nextElementSibling as HTMLSelectElement | null;
        const isCountrySelect = originalSelect?.classList.contains("country-select") ?? false;
        if (!isCountrySelect) return;

        const listWrapper = selectWrapper.querySelector(".form-list-wrapper") as HTMLDivElement;
        const listEl = listWrapper.querySelector(".form-list") as HTMLDivElement;
        const listUl = listEl.querySelector("ul") as HTMLUListElement;

        const searchContainer = document.createElement("div");
        searchContainer.classList.add("form-search");
        const searchInput = document.createElement("input");
        searchInput.type = "text";
        searchInput.classList.add("form-search__input");
        searchInput.placeholder = "Search country or code…";
        searchInput.setAttribute("aria-label", "Search country or dial code");
        searchContainer.appendChild(searchInput);

        listEl.before(searchContainer);

        const items = Array.from(
            listUl.querySelectorAll<HTMLLIElement>("li[data-type='js-single-select']")
        );

        const getSearchText = (li: HTMLLIElement) => {
            const visible = (li.textContent || "").toLowerCase();
            const dial = (li.dataset.dial || "").toLowerCase();
            const name = (li.dataset.name || "").toLowerCase();
            const value = (li.dataset.value || "").toLowerCase();
            return `${visible} ${dial} ${name} ${value}`.trim();
        };

        const filter = (q: string) => {
            const query = q.trim().toLowerCase();

            if (query === "") {
                items.forEach((li) => (li.style.display = ""));
                return;
            }

            items.forEach((li) => {
                const text = getSearchText(li);
                li.style.display = text.includes(query) ? "" : "none";
            });
        };

        searchInput.addEventListener("input", () => filter(searchInput.value));

        const focusSearchIfOpen = () => {
            if (selectWrapper.classList.contains("expanded")) {
                setTimeout(() => searchInput.focus(), 0);
            }
        };
        selectWrapper.addEventListener("click", focusSearchIfOpen);
        selectWrapper.addEventListener("keydown", (e) => {
            if (e.target === searchInput) return;

            if ((e.key === "Enter" || e.key === " ") && !selectWrapper.classList.contains("expanded")) {
                e.preventDefault();
                setTimeout(() => searchInput.focus(), 0);
            }
        });

        searchInput.addEventListener("keydown", (e) => {
            if (e.key === " " || e.key === "Enter") {
                e.stopPropagation();
                if (e.key === "Enter") e.preventDefault();
            }
            if (e.key === "Escape") {
                e.stopPropagation();
            }
        });
    };

    toggleCustomSelect = (select: HTMLDivElement) => {
        const selectHeader = select.querySelector(".form-header") as Element;
        select.addEventListener("keydown", (event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                toggle();
                listTabEnable();
            }
        });
        selectHeader.addEventListener("click", () => {
            toggle();
        });
        function listTabEnable() {
            select.querySelectorAll(".form-list li").forEach((item) => {
                item.setAttribute("tabindex", "0");
                const target = item as HTMLElement;
                target.addEventListener("keydown", function (event: KeyboardEvent) {
                    if (event.key === "Enter") {
                        event.preventDefault();
                        (this).click();
                        toggle();
                    }
                });
            });
        }
        function removeTabIndex() {
            setTimeout(() => {
                select.querySelectorAll(".form-list li").forEach((item) => {
                    item.removeAttribute("tabindex");
                });
            }, 100);
        }
        function toggle() {
            select.classList.toggle("expanded");

            const isDesktop = document.documentElement.clientWidth > 800;
            const threshold = isDesktop ? 300 : 220;
            const isAboveThreshold =
                document.documentElement.clientHeight -
                selectHeader.getBoundingClientRect().bottom <
                threshold;

            if (isAboveThreshold) {
                select.classList.add("above");
            } else {
                select.classList.remove("above");
            }
            if (!select.classList.value.includes("expanded")) {
                removeTabIndex();
            }
        }
    };

    nativeSelects = () => {
        const selects = this.rootElement.querySelectorAll<HTMLSelectElement>(
            ".Form__MainBody select"
        );

        selects.forEach((select: HTMLSelectElement) => {
            select.addEventListener("change", () => {
                if (document.documentElement.clientWidth < 800) {
                    const parentElement = select.parentElement;
                    if (!parentElement) return;

                    const element = parentElement.querySelector(
                        `li[data-value='${select.value}']`
                    );

                    if (select.hasAttribute("multiple")) {
                        if (element) {
                            (element as HTMLElement).click();
                        } else {
                            const formHeaderSpan =
                                parentElement.querySelector(".form-header span");
                            if (formHeaderSpan) {
                                formHeaderSpan.innerHTML = select.options[0].innerHTML;
                            }
                        }
                    } else if (select.value !== "") {
                        if (element) {
                            (element as HTMLElement).click();
                        }
                    }
                }
            });
        });
    };

    decodeHTMLEntities = (str: string) => {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = str;
        return textarea.value;
    };

    handleOptionSelect = (select: HTMLDivElement, isMultiple: boolean) => {
        const originalSelect = select.nextElementSibling;
        const isCountrySelect = originalSelect?.classList.contains("country-select") || false;

        if (isMultiple) {
            const options = select.querySelectorAll("li[data-type='js-multi-select']");

            options.forEach((option) => {
                option.addEventListener("click", (e) => {
                    const target = e.target as HTMLElement; // Cast e.target to HTMLElement
                    target.classList.toggle("selected");
                    setOptionState();
                    originalSelect?.dispatchEvent(new Event("change"));
                });
            });
            setOptionState();
        } else {
            const selectTitleElement = select.querySelector(".form-header span") as HTMLElement;
            const originalSelectOptions = select.nextElementSibling?.querySelectorAll(
                "option:not([disabled='disabled'])"
            ) as NodeListOf<HTMLElement>;
            const options = select.querySelectorAll("li[data-type='js-single-select']");

            options.forEach((option) => {
                option.addEventListener("click", (e) => {
                    const target = e.target as HTMLElement;
                    const previousSelectedItem = select.querySelector(".selected");

                    select.classList.add("has-selection");

                    if (previousSelectedItem !== null) {
                        previousSelectedItem.classList.remove("selected");
                    }

                    target.classList.add("selected");

                    // Show flag + value for country-select
                    if (selectTitleElement) {
                        if (isCountrySelect) {
                            const flag = target.dataset.flag || "";
                            const value = target.dataset.value || "";
                            selectTitleElement.innerHTML = flag
                                ? `<img src="${flag}"/> ${value}`
                                : value;
                        } else {
                            const decodedValue = this.decodeHTMLEntities(
                                target.querySelector("span")?.innerHTML ?? ""
                            );
                            selectTitleElement.innerHTML = decodedValue;
                        }
                    }

                    select.classList.remove("expanded");

                    if (document.documentElement.clientWidth > 800) {
                        originalSelectOptions?.forEach((selectOption) => {
                            const option = selectOption as HTMLOptionElement;
                            if (target.dataset.value === option.value) {
                                option.selected = true;
                            } else {
                                option.selected = false;
                            }
                        });

                        originalSelect?.dispatchEvent(new Event("change"));
                    }
                });
            });
        }
        function setOptionState() {
            const selectedItems = select.querySelectorAll(".selected");
            const originalSelectOptions = select.nextElementSibling?.querySelectorAll(
                "option:not([disabled='disabled'])"
            ) as NodeListOf<HTMLElement>;
            const selectTitleElement = select.querySelector(".form-header span");
            const selectTitle = selectTitleElement?.getAttribute("data-title") ?? "";

            if (document.documentElement.clientWidth > 800) {
                originalSelectOptions.forEach((originalSelectOption) => {
                    (originalSelectOption as HTMLOptionElement).selected = false;
                });

                selectedItems.forEach((selectedItem) => {
                    const selectedItemElement = selectedItem as HTMLElement;
                    originalSelectOptions.forEach((originalSelectOption) => {
                        if (
                            selectedItemElement.dataset.value ===
                            (originalSelectOption as HTMLOptionElement).value
                        ) {
                            (originalSelectOption as HTMLOptionElement).selected = true;
                        }
                    });
                });
            }

            if (selectedItems.length === 0) {
                if (selectTitleElement) {
                    selectTitleElement.innerHTML = selectTitle;
                }
                select.classList.remove("has-selection");
            } else if (selectedItems.length === 1) {
                if (selectTitleElement) {
                    if (isCountrySelect) {
                        const flag = (selectedItems[0] as HTMLElement).dataset.flag || "";
                        const value = (selectedItems[0] as HTMLElement).dataset.value || "";
                        selectTitleElement.innerHTML = flag
                            ? `${flag} ${value}`
                            : value;
                    } else {
                        selectTitleElement.innerHTML =
                            (selectedItems[0] as HTMLElement).querySelector(".textbox")?.innerHTML ?? "";
                    }
                }
                select.classList.add("has-selection");
            } else {
                let combinedText = "";
                selectedItems.forEach((selectedItem, index) => {
                    if (index === 0) {
                        combinedText = isCountrySelect
                            ? (selectedItem as HTMLElement).dataset.value || ""
                            : (selectedItem as HTMLElement).querySelector(".textbox")?.innerHTML ?? "";
                    } else {
                        combinedText += `, ${isCountrySelect
                            ? (selectedItem as HTMLElement).dataset.value || ""
                            : (selectedItem as HTMLElement).querySelector(".textbox")?.innerHTML ?? ""
                            }`;
                    }
                });

                if (selectTitleElement) {
                    selectTitleElement.innerHTML = combinedText;
                }
                select.classList.add("has-selection");
            }

            if (document.documentElement.clientWidth < 800) {
                const selectedOptions = Array.from(originalSelectOptions)
                    .filter((option) => (option as HTMLOptionElement).selected)
                    .map((option) =>
                        isCountrySelect
                            ? (option as HTMLOptionElement).value
                            : (option as HTMLOptionElement).innerHTML
                    );

                if (selectTitleElement) {
                    selectTitleElement.innerHTML = selectedOptions.join(", ");
                }
            }
        }
    };

    bindCountryCodeToHiddenInput(root?: Document | HTMLElement): void {
        const scope = root || document;

        const wrappers = scope.querySelectorAll<HTMLElement>('.phone-input-wrapper');
        wrappers.forEach((wrapper) => {
            const form = wrapper.closest('form');
            if (!form) return;

            const hidden = form.querySelector<HTMLInputElement>('input.country-code[type="hidden"]');
            if (!hidden) return;

            const select = wrapper.querySelector<HTMLSelectElement>('select.country-select');
            const list = wrapper.querySelector<HTMLElement>('.form-list');

            const getDialingCodeFromSelect = (): string | null => {
                return select?.value?.trim() || null;
            };

            const getDialingCodeFromList = (): string | null => {
                if (!list) return null;

                const active =
                    list.querySelector<HTMLElement>('[data-type="js-single-select"].is-active') ||
                    list.querySelector<HTMLElement>('[data-type="js-single-select"][aria-selected="true"]');
                return active?.getAttribute('data-dial')?.trim() || null;
            };

            const getDialingCodeFromHeader = (): string | null => {
                const header = wrapper.querySelector<HTMLElement>('.form-header [data-title]');
                if (!header?.textContent) return null;

                const match = new RegExp(/\+\d{1,4}\b/).exec(header.textContent);
                return match?.[0] || null;
            };

            const sync = () => {
                const dialingCode =
                    getDialingCodeFromSelect() ||
                    getDialingCodeFromList() ||
                    getDialingCodeFromHeader();

                if (!dialingCode) return;

                hidden.value = dialingCode;
                hidden.setAttribute('data-f-datainput', '');
                hidden.dispatchEvent(new Event('input', { bubbles: true }));
                hidden.dispatchEvent(new Event('change', { bubbles: true }));
            };

            sync();

            if (select) {
                select.addEventListener('change', () => {
                    sync();
                });
            }

            if (list) {
                list.addEventListener('click', (e) => {
                    const target = e.target;
                    if (!(target instanceof HTMLElement)) return;
                    const item = target.closest<HTMLElement>('[data-type="js-single-select"]');
                    if (!item) return;

                    list.querySelectorAll<HTMLElement>('[data-type="js-single-select"]').forEach((el) => el.classList.remove('is-active'));
                    item.classList.add('is-active');

                    const dialingCode = item.getAttribute('data-dial');
                    if (dialingCode && select) {
                        const match = Array.from(select.options).find((o) => o.value === dialingCode);
                        if (match) {
                            select.value = match.value;
                            select.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                    }

                    sync();
                });
            }

            const submitHandler = (e: Event) => {
                e.preventDefault();
                sync();

                // Use requestAnimationFrame to defer form submission until after sync() completes and DOM updates are flushed
                requestAnimationFrame(() => {
                    form.removeEventListener('submit', submitHandler);
                    form.requestSubmit();
                });
            };

            form.addEventListener('submit', submitHandler);
        });
    }

    phoneNumberCountryCodeConcat(root: Document | ParentNode = document): void {
        const doc: Document = (root as Document) || document;

        const closest = (el: Element | null, selector: string): Element | null => {
            if (!el) return null;
            if (typeof el.closest === 'function') return el.closest(selector);
            let node: Element | null = el;
            while (node) {
                if (node.matches(selector)) return node;
                node = node.parentElement;
            }
            return null;
        };

        const concatPhonesOnce = (form: Element | null): void => {
            if (!form) return;
            if (!form.classList.contains('ValidationSuccess')) return;

            const wrappers = form.querySelectorAll('.phone-input-wrapper');
            wrappers.forEach((wrapper) => {
                const select = wrapper.querySelector<HTMLSelectElement>('select.country-select');
                const input = wrapper.querySelector<HTMLInputElement>('input.FormTextbox__Input[type="tel"]');
                if (!select || !input) return;

                const cc = (select.value || '').trim();
                const val = (input.value || '').trim();

                if (!cc || !val) return;

                if (val.startsWith(cc) || val.startsWith('+')) return;

                input.value = cc + val;
            });
        };

        const scheduleConcat = (triggerEl: Element | null): void => {
            const form = closest(triggerEl, 'form.EPiServerForms, .EPiServerForms') ||
                doc.querySelector('form.EPiServerForms');
            requestAnimationFrame(() => {
                setTimeout(() => concatPhonesOnce(form), 0);
            });
        };

        doc.addEventListener('click', (ev: MouseEvent) => {
            const target = ev.target as Element | null;
            if (!target) return;
            const submitBtn = closest(target, '.FormSubmitButton[data-f-type="submitbutton"], button[type="submit"], input[type="submit"]');
            if (!submitBtn) return;
            scheduleConcat(submitBtn);
        }, true);

        doc.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            const active = doc.activeElement;
            if (!active) return;
            const container = closest(active, 'form.EPiServerForms, .EPiServerForms, .Form__MainBody') || doc.documentElement;
            scheduleConcat(container);
        }, true);
    };
}